// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Change
    skeletonStyle?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
    },
    subCategoryItem: {
        width: [12, 12, 4, 4, 2],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
            `,
        },
    },
};

const styles = categoryDetailSubCategoriesStyles;

// XNG-Change
const categoryDetailSubCategoriesMapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);

    return {
        isLoading: getCatalogPageStateByPath(state, categoryPath)?.isLoading,
    };
};
// </XNG-Changes>

const CategoryDetailSubCategories = ({
    category,
    fields: { showImages },
    isLoading,
}: Props & ReturnType<typeof categoryDetailSubCategoriesMapStateToProps>) => {
    if (isLoading) {
        return <Skeleton height="100px" {...styles.skeletonStyle} />;
    }
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    // XNG-Changes: change structure
    return (
        <StyledWrapper {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <SubCategoryLink key={subCategoryId} categoryId={subCategoryId} showImages={showImages} />
            ))}
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    // XNG-Changes: change structure
    return (
        <>
            {showImages && (
                <StyledWrapper {...styles.subCategoryImageItem}>
                    {category.smallImagePath && (
                        <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.subCategoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
        </>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(categoryDetailSubCategoriesMapStateToProps)(withCategory(CategoryDetailSubCategories)),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
